import React, { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Member, Organization } from "../common/ObjectTypes";
import { BackArrow, CopyToClipboard } from "../common/ProjectIcons";
import NoResults from "../common/NoResults";
import { HoverTooltip } from "../common/HoverTooltip";
import { tableClasses, buttonClasses, skeletonClasses, paginationClasses, listPageClasses } from "../common/tailwindClassnames";
import Pagination from "../common/Pagination";
import { copyToClipboard } from "../common/copyToClipboard";

type OrganizationDetailsProps = {
    orgMembers: Member[],
    organization: Organization,
    onBackArrow: MouseEventHandler,
    onClickInvite: MouseEventHandler,
    onClickMember: Function,
    onToggleModal: MouseEventHandler,
    onToggleRemoveMemberModal: Function,
    currentMember: Member,
    superuser: boolean,
    pageLoaded: boolean,
    // `orgMembers` is the current page; these describe the full member list it
    // was sliced from, so the paginator can show real totals.
    totalCount: number,
    offset: number,
    take: number,
    onPageClicked: Function
}

const OrganizationDetails = ({
    orgMembers,
    organization,
    onBackArrow,
    onToggleModal,
    onClickInvite,
    onClickMember,
    onToggleRemoveMemberModal,
    currentMember,
    superuser,
    pageLoaded,
    totalCount,
    offset,
    take,
    onPageClicked
}: OrganizationDetailsProps) => {
    const { t } = useTranslation();
    return (
        <div className="transition-all duration-300">
            <div className={listPageClasses.band}>
                <div className={listPageClasses.bandRow}>
                    <div>
                        <div className="text-brand-accent-green flex flex-row text-sm mb-1 cursor-pointer hover:underline w-min transition-colors duration-300"
                            onClick={onBackArrow}>
                            <BackArrow />  <label className="ml-2 cursor-pointer">{t("Workspaces")}</label>
                        </div>
                        <h1 className={listPageClasses.title}>{organization.name}</h1>
                    </div>
                    {(currentMember.role === "admin" || superuser) && <div className="flex gap-3">
                        <button className={buttonClasses.lightGreen}
                            onClick={onToggleModal}>
                            {t("Update")}
                        </button>
                        <button className={buttonClasses.primary}
                            onClick={onClickInvite}
                        >
                            {t("Add User")}
                        </button>
                    </div>}
                </div>
            </div>
            <div className={listPageClasses.body}>
            <div className="max-w-list-table mx-auto">
                <table className={tableClasses.container}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={tableClasses.headerCell}>
                                {t("Name")}
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Email")}
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Role")}
                            </th>
                            {(currentMember.role === "admin" || superuser) && <th className={tableClasses.headerCell}>
                                &nbsp;
                            </th>}
                        </tr>
                    </thead>
                    {!pageLoaded ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className="border-b border-teal-t30 last:border-b-0 bg-white" data-testid="loading">
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-48 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-16 ${skeletonClasses.base}`} /></td>
                                    {(currentMember.role === "admin" || superuser) && (
                                        <td className={tableClasses.cell}><div className={`h-4 w-6 ${skeletonClasses.base}`} /></td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        :
                        <tbody className="text-sm transition-colors duration-300">
                            {orgMembers.map((org_member: Member) => {
                                return (
                                    <tr key={org_member.id ? org_member.id.toString() : Math.random()}
                                        className="border-b border-teal-t30 last:border-b-0 hover:bg-greyscale-11 bg-white transition-colors duration-300">
                                        <td className={tableClasses.cell}
                                            onClick={() => onClickMember(org_member.id)}>
                                            {org_member.full_name}
                                        </td>
                                        <td className={tableClasses.cell}>
                                            <div className="flex flex-row items-center gap-2">
                                                <span
                                                    className="cursor-pointer"
                                                    onClick={() => onClickMember(org_member.id)}>
                                                    {org_member.email}
                                                </span>
                                                {org_member.email && (
                                                    <button
                                                        type="button"
                                                        data-testid="OrganizationDetails-copyEmail-button"
                                                        // The email beside it opens the member.
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(org_member.email as string, t, t("Email"));
                                                        }}
                                                        className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                                                        aria-label={t("Copy to clipboard")}
                                                    >
                                                        <CopyToClipboard />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        {/* <td className="p-3">
                                                Non mvp
                                            </td> */}
                                        <td className={tableClasses.cell}
                                            onClick={() => onClickMember(org_member.id)}>
                                            {org_member.role}
                                        </td>
                                        <td className={tableClasses.cell + " text-right"}>
                                            {(currentMember.role === "admin" || superuser) ? (
                                                <button
                                                    type="button"
                                                    className={buttonClasses.lightRed}
                                                    onClick={() => onToggleRemoveMemberModal(org_member.id)}
                                                >
                                                    {t("Remove")}
                                                </button>
                                            ) : (
                                                <HoverTooltip position="top-right" width="w-52" content={t("Only admins can manage workspace membership.")}>
                                                    <button
                                                        type="button"
                                                        disabled
                                                        className={buttonClasses.lightRed}
                                                    >
                                                        {t("Remove")}
                                                    </button>
                                                </HoverTooltip>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>}
                    {pageLoaded && orgMembers.length === 0 &&
                        <tbody><tr><td colSpan={(currentMember.role === "admin" || superuser) ? 4 : 3} className="py-16">
                            <NoResults
                                headerMsg={t("No Members Found")}
                                labelMsg={t("You may need to add your first member, or contact system admin.")}
                            />
                        </td></tr></tbody>}
                </table>
                {totalCount > take && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div>
        </div>
    );
};

export default OrganizationDetails;